import { motion } from 'framer-motion';
import { useState } from 'react';
import { useComment, useComments } from '@/hooks/useGallery';
import { useDisplayName, ANONYMOUS_NAME } from '@/hooks/useDisplayName';
import { useBanStatus } from '@/hooks/useBanStatus';
import { supabase } from '@/lib/supabase';
import { getDeviceId } from '@/lib/device';
import { formatRoHour } from '@/lib/time';

interface CommentListProps {
  photoId: string;
}

/**
 * A single comment line: the username is the hero (accent-coloured, bold, monospace
 * chip-like) and the comment text sits softly underneath. Date in mono on the right.
 */
function CommentItem({ name, text, time }: { name: string; text: string; time?: number | string }) {
  return (
    <motion.li
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="border-l-2 border-accent/40 pl-sm py-xs"
    >
      <p className="flex items-baseline gap-sm">
        <span className="font-serif text-sm font-semibold text-accent">
          {name}
        </span>
        {time && (
          <span className="font-mono text-[0.65rem] text-fg-soft">
            · {formatRoHour(time)}
          </span>
        )}
      </p>
      <p className="mt-xs text-sm leading-snug text-fg">
        {text}
      </p>
    </motion.li>
  );
}

export default function CommentList({ photoId }: CommentListProps) {
  const { name: savedName, setName: saveName, loading: displayNameLoading } = useDisplayName();
  const { data: ban } = useBanStatus();
  const { data: serverComments = [] } = useComments(photoId);
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [recent, setRecent] = useState<{ id?: string; name: string; text: string; time: string }[]>([]);
  const { mutate: postComment, isPending } = useComment(photoId);

  const isBanned = ban?.is_banned === true;

  /**
   * Re-citeste display_name-ul din Supabase, sincron, pentru device-ul curent.
   * Fix pt. cursa: useDisplayName porneste cu localStorage-ul instant, dar
   * fetch-ul remote poate fi mai lent decat Submit. Daca userul are numele
   * salvat in visitors dar localStorage-ul e gol (ex: dispozitiv nou, cache
   * sters, alt browser), Submit-ul ar fi trimis "Anonim" in loc de numele real.
   */
  const fetchSavedName = async (): Promise<string> => {
    const deviceId = getDeviceId();
    if (!deviceId) return '';
    try {
      const { data, error } = await supabase
        .from('visitors')
        .select('display_name')
        .eq('device_id', deviceId)
        .maybeSingle();
      if (error) return '';
      return (data?.display_name ?? '').toString();
    } catch {
      return '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;

    const inputName = name.trim();
    // Daca hook-ul zice ca savedName e gol, mai intai verifica remote
    // (poate inca n-a terminat fetch-ul la momentul submit-ului)
    const remoteName = savedName ? '' : await fetchSavedName();
    const effectiveSaved = savedName || remoteName;
    const finalName = ((editingName && inputName) ? inputName : (inputName || effectiveSaved || ANONYMOUS_NAME)).trim() || ANONYMOUS_NAME;

    if (finalName && finalName !== savedName && finalName !== ANONYMOUS_NAME) {
      try {
        await saveName(finalName);
        setEditingName(false);
      } catch {
        // fail silent la sync
      }
    }

    const submittedText = text.trim();
    const now = new Date();
    const timeIso = now.toISOString();

    postComment(
      { author: finalName, body: submittedText },
      {
        onSuccess: () => {
          setRecent((prev) => [
            { id: `recent-${Date.now()}`, name: finalName, text: submittedText, time: timeIso },
            ...prev,
          ]);
          setText('');
        },
      }
    );
  };

  const showNameInput = !savedName || editingName;
  // Submit blocat cat hook-ul de display_name e in loading (cursa async).
  // Daca userul a introdus un nume in input, el trece inainte.
  const canComment = text.trim().length > 0 && (!displayNameLoading || !!name.trim() || !!savedName);

  const allComments = [
    ...serverComments.map((c) => ({
      id: String(c.id),
      name: c.author,
      text: c.body,
      time: c.created_at,
    })),
    ...recent.filter(
      (r) => !serverComments.some((sc) => sc.body === r.text && sc.author === r.name)
    ),
  ];

  return (
    <div className="mt-md space-y-md">
      {allComments.length > 0 ? (
        <ul className="space-y-xs max-h-60 overflow-y-auto pr-1">
          {allComments.map((c, i) => (
            <CommentItem key={c.id ?? i} name={c.name} text={c.text} time={c.time} />
          ))}
        </ul>
      ) : (
        <p className="py-xs text-xs text-fg-soft italic">Încă nu sunt comentarii. Fii primul care lasă un gând!</p>
      )}

      <form onSubmit={handleSubmit} className="space-y-sm border-t border-border pt-md">
        {isBanned ? (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="rounded-md border border-error/40 bg-error/5 p-sm text-sm"
            role="alert"
          >
            <p className="font-medium text-error">Cont suspendat temporar</p>
            <p className="mt-xs text-xs text-fg-soft">
              Dreptul de a comenta a fost suspendat. Discută cu mirele sau mireasa.
            </p>
          </motion.div>
        ) : (
          <>
            {showNameInput ? (
              <div className="input-floating">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder=" "
                  maxLength={50}
                  className="w-full border-0 border-b-2 border-border bg-transparent px-0 py-xs text-sm outline-none focus:border-accent"
                />
                <label className="text-sm">Numele tău (opțional)</label>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-xs text-fg-soft">
                  Comentezi ca <span className="font-serif italic text-fg">{savedName}</span>
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setName(savedName);
                    setEditingName(true);
                  }}
                  className="text-xs text-fg-soft underline-offset-2 hover:text-fg hover:underline"
                >
                  Schimbă numele
                </button>
              </div>
            )}

            <div className="flex gap-2 items-end">
              <div className="input-floating flex-1">
                <input
                  type="text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder=" "
                  maxLength={500}
                  className="w-full border-0 border-b-2 border-border bg-transparent px-0 py-xs text-sm outline-none focus:border-accent"
                />
                <label className="text-sm">Scrie un comentariu...</label>
              </div>
              <motion.button
                type="submit"
                disabled={isPending || !canComment}
                whileTap={{ scale: 0.96 }}
                className="btn-primary text-xs px-3 py-1.5 shrink-0"
              >
                {isPending ? 'Trimit...' : 'Trimite'}
              </motion.button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

export { CommentItem };